import React, { useEffect, useState, useCallback, useRef } from 'react';

import { useSearchParams, useNavigate, Link } from 'react-router-dom';

import { PageShell } from '../../components/Ui';

import { useAuth } from '../../auth/AuthContext';

import { ipApi } from '../../services/api';

import { useLiveData } from '../../hooks/useLiveData';
import { useEmrSettings } from '../../hooks/useEmrSettings';

import { Q, queueRowId } from '../../config/queueStatus';

import { EMR_TABS, createEmrForm } from '../../config/emrCatalog';

import EmrPrintPrescription from '../../components/emr/EmrPrintPrescription';

import { buildEmrPayload, buildLabText, buildPrescriptionText, buildSoapFromForm, buildPharmacyLines, buildLabTests, mergeEmrDraft, mergePreVitals, normalizePmh } from '../../utils/emrHelpers';
import { runSafetyCheck } from '../../utils/pharmacySafety';

import EmrPatientBar from '../../components/emr/EmrPatientBar';

import EmrTabPanel from '../../components/emr/EmrTabPanel';

import EmrMagicBox from '../../components/emr/EmrMagicBox';
import DentalChart from '../../components/emr/DentalChart';
import PrivateNotesPanel from '../../components/emr/PrivateNotesPanel';
import HealthAssessmentPanel from '../../components/emr/HealthAssessmentPanel';
import OphthalmologyPanel from '../../components/emr/OphthalmologyPanel';
import WhatsappScribePanel from '../../components/emr/WhatsappScribePanel';

import MedicalCertificateModal from '../../components/emr/MedicalCertificateModal';

import { visibleEmrTabs, effectivePrintSettings } from '../../config/emrSettings';
import DocAssistPanel from '../../components/emr/DocAssistPanel';
import PediatricDoseCalculator from '../../components/emr/PediatricDoseCalculator';
import { emptyRxLine } from '../../config/emrCatalog';
import { doctorHasDentalWorkspace } from '../../utils/dentalSpecialty';

import { useHospitalProfile } from '../../hooks/useHospitalProfile';

import { canAccess, landingPathFor } from '../../permissions';
import { buildIpdAdmitLink } from '../../utils/opIpLink';



export function EmrPage() {

  const { user } = useAuth();

  const { hospital: hospitalProfile } = useHospitalProfile();

  const navigate = useNavigate();

  const [params] = useSearchParams();

  const recordIdRef = useRef(`EMR-${Date.now()}`);

  const createdAtRef = useRef(new Date().toISOString());

  const pharmacySentRef = useRef(false);



  const patientName = params.get('patient') || '';

  const patientIdParam = params.get('patientId') || '';

  const queueId = params.get('queue') || '';

  const readOnly = user?.role === 'nurse';



  const [tab, setTab] = useState('consult');

  const [form, setForm] = useState(() =>
    createEmrForm({ patientName, patientId: patientIdParam || '' })
  );
  const [searchResults, setSearchResults] = useState([]);

  const [msg, setMsg] = useState('');

  const [busy, setBusy] = useState(false);

  const [showPrint, setShowPrint] = useState(false);

  const [showCert, setShowCert] = useState(false);

  const [queuePreVitals, setQueuePreVitals] = useState(null);

  const [patientRecord, setPatientRecord] = useState(null);



  const { settings: emrSettings, genieMeds, error: emrSettingsError, reload: reloadEmrSettings } = useEmrSettings();
  const printSettings = effectivePrintSettings(emrSettings, user);
  const emrTabs = visibleEmrTabs(emrSettings).length ? visibleEmrTabs(emrSettings) : EMR_TABS;
  const pid = form.patientId?.trim() || '';
  const consultReady = !!(form.patientName?.trim() && pid);
  const { data: historyData, loading: historyLoading, error: historyError, reload: reloadHistory } = useLiveData(
    () => (pid ? ipApi.getEmrRecords(pid, 25) : Promise.resolve({ records: [] })),
    [pid]
  );

  const pastVisits = historyData?.records || [];



  const { data: invData, error: invError } = useLiveData(() => ipApi.getPharmacyInventory(), []);

  const inventory = invData?.items || [];

  const { data: labCatData, error: labCatError } = useLiveData(() => ipApi.getLabCatalog(), []);

  const labCatalog = labCatData?.items || [];
  const catalogLoadError = invError || labCatError;

  const { data: queueData } = useLiveData(() => ipApi.getQueue({ limit: 120 }), []);

  const resolveQueueId = useCallback(() => {
    if (queueId) return queueId;
    const pid = form.patientId || patientIdParam;
    if (!pid) return '';
    const today = new Date().toISOString().slice(0, 10);
    const row = (queueData?.queue || []).find((q) =>
      q.patientId === pid
      && (q.visitDate || '').slice(0, 10) === today
      && q.status !== Q.COMPLETED
      && q.status !== Q.CANCELLED);
    return row ? queueRowId(row) : '';
  }, [queueId, form.patientId, patientIdParam, queueData]);



  const appliedQueueRef = useRef('');

  const applyPatient = useCallback((match) => {
    if (!match) return;
    // Hard reset clinical state — never carry previous patient's Rx/complaint onto a new UHID
    recordIdRef.current = `EMR-${Date.now()}`;
    createdAtRef.current = new Date().toISOString();
    pharmacySentRef.current = false;
    appliedQueueRef.current = '';
    setPatientRecord(match);
    setSearchResults([]);
    setForm(createEmrForm({
      patientName: match.name,
      patientId: match.patientId,
      patientAge: match.age,
      patientGender: match.gender,
      patientPhone: match.phone,
      history: {
        allergies: match.allergies || '',
        currentMeds: match.currentMeds || '',
        pmh: normalizePmh(match.chronicConditions),
      },
    }));
    setMsg('');
  }, []);

  // URL patient change must also remount clinical state
  useEffect(() => {
    recordIdRef.current = `EMR-${Date.now()}`;
    createdAtRef.current = new Date().toISOString();
    pharmacySentRef.current = false;
    appliedQueueRef.current = '';
  }, [patientIdParam]);

  const applyQueueRow = useCallback((row) => {
    if (!row || readOnly) return;
    if (row.preVitals) {
      setQueuePreVitals(row.preVitals);
      setForm((f) => mergePreVitals(f, row.preVitals));
    }
    if (row.emrDraft) {
      try {
        const draft = typeof row.emrDraft === 'string' ? JSON.parse(row.emrDraft) : row.emrDraft;
        setForm((f) => mergeEmrDraft(f, draft));
        setMsg('Resumed paused consultation.');
      } catch { /* ignore bad draft */ }
    }
    if (row.patientId) {
      setForm((f) => ({
        ...f,
        patientId: f.patientId || row.patientId,
        patientName: f.patientName || row.patientName || row.name || '',
        patientPhone: f.patientPhone || row.patientPhone || '',
        patientAge: f.patientAge || row.age || '',
        patientGender: f.patientGender || row.gender || '',
      }));
    }
  }, [readOnly]);

  const loadPatient = useCallback(async (search) => {
    if (!search || search.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const data = await ipApi.getPatients(search, 20);
      const q = search.toLowerCase().trim();
      const matches = (data.patients || []).filter(
        (p) => p.patientId?.toLowerCase().includes(q)
          || p.name?.toLowerCase().includes(q)
          || String(p.phone || '').includes(q)
      );
      if (!matches.length) {
        setSearchResults([]);
        setMsg('No patient found in registry — register at reception first.');
        return;
      }
      if (matches.length === 1) {
        applyPatient(matches[0]);
        return;
      }
      setSearchResults(matches);
      setMsg(`${matches.length} patients found — pick one below.`);
    } catch (e) {
      setMsg(e.message || 'Could not load patient.');
    }
  }, [applyPatient]);



  useEffect(() => {
    if (!emrTabs.some((t) => t.id === tab)) setTab(emrTabs[0]?.id || 'consult');
  }, [emrTabs, tab]);

  useEffect(() => {
    if (patientIdParam) loadPatient(patientIdParam);
    else if (patientName && patientName.length >= 2) loadPatient(patientName);
  }, [patientIdParam, patientName, loadPatient]);

  useEffect(() => {
    if (readOnly || !queueData?.queue) return;
    const qid = resolveQueueId();
    if (!qid || appliedQueueRef.current === qid) return;
    const row = (queueData.queue || []).find((q) => queueRowId(q) === qid);
    if (!row) return;
    appliedQueueRef.current = qid;
    applyQueueRow(row);
    if (row.status === Q.WAITING_DOCTOR) {
      ipApi.patchQueue(qid, Q.IN_CONSULTATION).catch((e) => {
        setMsg(e.message || 'Could not mark consultation as started.');
      });
    }
  }, [queueData, resolveQueueId, readOnly, applyQueueRow]);



  const syncPatientProfile = async () => {
    if (!form.patientId?.trim()) return;
    try {
      const pmh = form.history.pmh;
      await ipApi.patchPatient(form.patientId, {
        allergies: form.history.allergies,
        currentMeds: form.history.currentMeds,
        chronicConditions: Array.isArray(pmh) ? pmh.join(', ') : pmh,
      });
    } catch (e) {
      setMsg(e.message || 'Could not sync patient profile.');
    }
  };

  const pauseConsult = async () => {
    const qid = resolveQueueId();
    if (!qid || readOnly) return;

    setBusy(true);

    try {
      // Never put doctor-private notes on the shared OP queue draft
      const { privateNotes, ...safeForm } = form;
      await ipApi.patchQueue(qid, {
        emrDraft: JSON.stringify(safeForm),
        pausedAt: new Date().toISOString(),
        status: Q.IN_CONSULTATION,
      });

      setMsg('Consultation paused — data saved. Resume from doctor queue.');

      navigate(landingPathFor(user?.role));

    } catch (e) {

      setMsg(e.message);

    } finally {

      setBusy(false);

    }

  };



  const bookFollowUp = async () => {

    const days = prompt('Follow-up after how many days?', '7');

    if (!days) return;

    const d = new Date();

    d.setDate(d.getDate() + Number(days));

    try {

      await ipApi.addToQueue({

        patientName: form.patientName,

        patientId: form.patientId,

        patientPhone: form.patientPhone,

        age: form.patientAge,

        gender: form.patientGender,

        doctor: user?.name || 'Doctor',

        visitDate: d.toISOString().slice(0, 10),

        visitType: 'Follow-up',

        reason: form.assessment?.primary || 'Follow-up',

        status: Q.BOOKED,

        hospitalId: user.hospitalId,

      });

      setMsg(`Follow-up booked for ${d.toLocaleDateString('en-IN')}.`);

    } catch (e) {

      setMsg(e.message);

    }

  };



  const saveEmr = async (complete = false) => {
    if (readOnly) return;
    if (!form.patientId?.trim()) {
      setMsg('Link patient to UHID before saving — search registry above.');
      return;
    }
    if (complete) {
      const drugNames = (form.rxLines || []).filter((r) => r.drug).map((r) => r.drug);
      if (drugNames.length) {
        const safety = runSafetyCheck({
          allergies: form.history?.allergies || patientRecord?.allergies || '',
          drugs: drugNames,
        });
        if (safety.blocking) {
          const detail = [
            ...safety.allergies.map((a) => `• Allergy: ${a.note}`),
            ...safety.interactions.map((i) => `• ${i.drugA} + ${i.drugB}: ${i.note}`),
          ].join('\n');
          if (!window.confirm(`⚠ CLINICAL SAFETY ALERT\n\n${detail}\n\nOverride and complete visit anyway?`)) {
            setMsg(`Blocked by safety check: ${safety.summary}`);
            return;
          }
        }
      }
    }
    setBusy(true);
    setMsg('');
    try {
      const payload = buildEmrPayload(form, user, resolveQueueId() || queueId, recordIdRef.current, createdAtRef.current);
      payload.status = complete ? 'completed' : 'saved';
      await ipApi.saveEmr(payload);
      await syncPatientProfile();
      await reloadHistory();
      const failed = [];
      if (complete) {
        // Auto-send Rx to pharmacy on complete (once per record — the manual
        // "→ Pharmacy" button also sets this flag to avoid duplicate orders).
        const rxLines = (form.rxLines || []).filter((r) => r.drug);
        if (rxLines.length && !pharmacySentRef.current) {
          try {
            const items = buildPrescriptionText(rxLines);
            const lines = buildPharmacyLines(rxLines, inventory);
            await ipApi.createPharmacyOrder({
              patientName: form.patientName,
              patientId: form.patientId,
              doctorName: user?.name,
              items,
              lines,
              rxLines,
              prescription: items,
              hospitalId: user.hospitalId,
              status: 'pending',
              source: 'emr',
              recordId: recordIdRef.current,
            });
            pharmacySentRef.current = true;
          } catch (pe) {
            failed.push(`pharmacy (${pe.message})`);
          }
        }
        const qid = resolveQueueId();
        if (qid) {
          try {
            await ipApi.patchQueue(qid, Q.COMPLETED);
          } catch (qe) {
            // EMR POST already completes the queue server-side — ignore redundant patch failures
            console.warn('Queue complete patch (optional):', qe.message);
          }
        }
        if (failed.length) {
          setMsg(`EMR saved, but these failed: ${failed.join('; ')}. Use "→ Pharmacy" to retry sending the Rx.`);
          setBusy(false);
          return; // stay on the page so the doctor can retry
        }
        setMsg('Visit saved & completed. Rx sent to pharmacy.');
        navigate(landingPathFor(user?.role));
      } else {
        setMsg('EMR saved.');
      }
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };



  const autoFillSoap = () => {

    if (readOnly) return;

    const soap = buildSoapFromForm(form);

    setForm((f) => ({ ...f, soap }));

    setTab('notes');

    setMsg('SOAP auto-filled from consult data.');

  };



  const sendPharmacy = async () => {

    if (readOnly) return;

    const items = buildPrescriptionText(form.rxLines);

    if (!items) { setMsg('Add prescription first.'); return; }

    const drugNames = (form.rxLines || []).filter((r) => r.drug).map((r) => r.drug);
    const safety = runSafetyCheck({
      allergies: form.history?.allergies || patientRecord?.allergies || '',
      drugs: drugNames,
    });
    if (safety.blocking) {
      const detail = [
        ...safety.allergies.map((a) => `Allergy: ${a.note}`),
        ...safety.interactions.map((i) => `Interaction: ${i.drugA} + ${i.drugB} — ${i.note}`),
      ].join('\n');
      if (!window.confirm(`⚠ CLINICAL SAFETY ALERT\n\n${detail}\n\nOverride and send to pharmacy anyway?`)) {
        setMsg(`Blocked by safety check: ${safety.summary}`);
        return;
      }
    }

    setBusy(true);

    try {

      const lines = buildPharmacyLines(form.rxLines, inventory);

      const matched = lines.filter((l) => l.itemId).length;

      await ipApi.createPharmacyOrder({

        patientName: form.patientName,

        patientId: form.patientId,

        doctorName: user?.name,

        items,

        lines,

        rxLines: form.rxLines.filter((r) => r.drug),

        prescription: items,

        hospitalId: user.hospitalId,

        status: 'pending',

        source: 'emr',

        recordId: recordIdRef.current,

      });

      setMsg(matched ? `Sent to pharmacy (${matched}/${lines.length} priced from inventory).` : 'Sent to pharmacy desk — set prices at the counter.');

      pharmacySentRef.current = true;

    } catch (err) {

      setMsg(err.message);

    } finally {

      setBusy(false);

    }

  };



  const sendLab = async () => {

    if (readOnly) return;

    const imagingRe = /x-?\s*ray|usg|ultrasound|\bct\b|mri|echo|mammogra|doppler|fluoroscop|pet.?ct|\becg\b|\bekg\b/i;
    const labOnly = (form.labOrders || []).filter((n) => !imagingRe.test(n));
    const test = buildLabText(labOnly, form.labNotes);

    if (!test) { setMsg('Select laboratory investigations first (imaging goes via → Radiology).'); return; }

    setBusy(true);

    try {

      const allNames = [...labOnly, ...(form.labNotes ? [form.labNotes] : [])];

      const tests = buildLabTests(allNames, labCatalog);

      await ipApi.createLabOrder({

        patientName: form.patientName,

        patientId: form.patientId,

        age: form.patientAge,

        gender: form.patientGender,

        referredBy: user.name,

        test,

        tests,

        labOrders: labOnly,

        hospitalId: user.hospitalId,

        status: 'ordered',

        source: 'emr',

        recordId: recordIdRef.current,

      });

      const priced = tests.filter((t) => t.rate > 0).length;

      setMsg(priced ? `Sent to laboratory (${priced}/${tests.length} priced from catalog).` : 'Sent to laboratory.');

    } catch (err) {

      setMsg(err.message);

    } finally {

      setBusy(false);

    }

  };

  const sendRadiology = async () => {
    if (readOnly) return;
    const imagingRe = /x-?\s*ray|usg|ultrasound|\bct\b|mri|echo|mammogra|doppler|fluoroscop|pet.?ct|\becg\b|\bekg\b/i;
    const studies = (form.labOrders || []).filter((n) => imagingRe.test(n));
    if (!studies.length) { setMsg('Select imaging (X-ray / USG / CT / MRI / Echo / ECG) first.'); return; }
    setBusy(true);
    try {
      const modalityOf = (name) => {
        const s = String(name || '').toLowerCase();
        if (/mri/.test(s)) return 'MRI';
        if (/\bct\b|pet/.test(s)) return 'CT';
        if (/usg|ultrasound|doppler/.test(s)) return 'USG';
        if (/echo/.test(s)) return 'Echo';
        if (/ecg|ekg/.test(s)) return 'ECG';
        if (/mammo/.test(s)) return 'Mammography';
        return 'X-Ray';
      };
      const amountOf = (mod) => ({ MRI: 6000, CT: 3500, USG: 800, Echo: 1500, ECG: 300, Mammography: 1200, 'X-Ray': 500 }[mod] || 500);
      for (const study of studies) {
        const modality = modalityOf(study);
        await ipApi.createRadiologyOrder({
          patientName: form.patientName,
          patientId: form.patientId,
          age: form.patientAge,
          gender: form.patientGender,
          referredBy: user.name,
          modality,
          study,
          bodyPart: study,
          amount: amountOf(modality),
          status: 'pending',
          source: 'emr',
          recordId: recordIdRef.current,
        });
      }
      setMsg(`Sent ${studies.length} imaging order${studies.length > 1 ? 's' : ''} to radiology.`);
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };



  const copyFromVisit = (visit) => {

    if (readOnly) return;

    setForm((f) => ({
      ...f,
      complaint: visit.complaint || f.complaint,
      hpi: visit.hpi ? { ...f.hpi, ...visit.hpi } : f.hpi,
      vitals: visit.vitals ? { ...f.vitals, ...visit.vitals } : f.vitals,
      labOrders: visit.labOrders?.length ? [...visit.labOrders] : f.labOrders,
      assessment: visit.assessment || { ...f.assessment, primary: visit.diagnosis || f.assessment.primary },
      rxLines: visit.rxLines?.length ? visit.rxLines.map((r) => ({ ...r })) : f.rxLines,
      plan: visit.plan ? { ...f.plan, ...visit.plan } : f.plan,
    }));
    setMsg('Copied diagnosis, vitals, Rx & labs from prior visit.');

  };



  const applyDocAssist = (s) => {

    if (readOnly) return;

    const adviceText = Array.isArray(s.advice) ? s.advice.filter(Boolean).join(' ') : (s.advice || '');

    setForm((f) => {
      const existingRx = (f.rxLines || []).filter((l) => l.drug);
      const suggested = (s.rx || []).map((drug) => ({ ...emptyRxLine(), drug: typeof drug === 'string' ? drug : (drug.drug || drug.name || '') })).filter((l) => l.drug);
      const mergedRx = [...existingRx];
      suggested.forEach((line) => {
        if (!mergedRx.some((e) => String(e.drug).toLowerCase() === String(line.drug).toLowerCase())) {
          mergedRx.push(line);
        }
      });
      return {
        ...f,
        assessment: { ...f.assessment, primary: s.diagnosis?.[0] || f.assessment.primary },
        labOrders: [...new Set([...(f.labOrders || []), ...(s.labs || [])])],
        plan: { ...f.plan, advice: [f.plan?.advice, adviceText].filter(Boolean).join(' ') },
        rxLines: mergedRx.length ? mergedRx : f.rxLines,
      };
    });

    setMsg('Clinical Assist suggestions applied — please review.');

  };



  const applyPediatricDose = (result) => {

    if (readOnly || !result?.rxLine) return;

    setForm((f) => ({

      ...f,

      rxLines: [...f.rxLines.filter((l) => l.drug), { ...emptyRxLine(), drug: result.drug, dose: result.dose, instructions: result.note }],

    }));

    setMsg('Pediatric dose added to Rx.');

  };



  const patientBar = {

    name: form.patientName,

    patientId: form.patientId,

    age: form.patientAge,

    gender: form.patientGender,

    phone: form.patientPhone,

    visitType: form.visitType,

  };



  const backPath = user?.role === 'doctor' ? '/doctor/queue' : landingPathFor(user?.role);



  return (

    <PageShell

      title="Electronic Medical Record"

      subtitle={readOnly ? `${user?.name} · view only` : `${user?.name} · full clinical documentation`}

      actions={

        <div className="row-actions">

          <Link to={backPath} className="btn btn-secondary btn-sm">← Back</Link>

          {canAccess(user?.role, '/ipd/admit') && (
            <Link
              to={buildIpdAdmitLink({
                patientName: form.patientName,
                patientId: form.patientId,
                queueId: resolveQueueId() || queueId,
                doctor: form.doctor || user?.name || '',
                reason: form.complaint || form.assessment?.primary || '',
                diagnosis: form.assessment?.primary || '',
                phone: form.patientPhone || '',
              })}
              className="btn btn-secondary btn-sm"
            >
              Admit IPD
            </Link>
          )}

        </div>

      }

    >

      <EmrPatientBar
        patient={patientBar}
        allergies={form.history.allergies}
        onSearch={loadPatient}
        searchResults={searchResults}
        onPickPatient={applyPatient}
        readOnly={readOnly}
      />

      {emrSettingsError && (
        <div className="error mb">
          Could not load Rx pad / EMR settings — {emrSettingsError}{' '}
          <button type="button" className="btn btn-sm btn-secondary" onClick={reloadEmrSettings}>Retry</button>
        </div>
      )}

      {historyError && consultReady && (
        <p className="hint error mb">
          Could not load visit history — {historyError}{' '}
          <button type="button" className="btn btn-sm btn-secondary" onClick={reloadHistory}>Retry</button>
        </p>
      )}
      {catalogLoadError && consultReady && (
        <p className="hint error mb">
          Pharmacy inventory / lab catalog failed to load — {catalogLoadError}. Rx suggestions and lab pickers may be incomplete.
        </p>
      )}
      {historyLoading && consultReady && pid && <p className="hint mb">Loading visit history…</p>}

      {!consultReady && (
        <div className="card" style={{ padding: 28, textAlign: 'center', marginBottom: 16, border: '1.5px dashed #cbd5e1', background: '#f8fafc' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>👤</div>
          <h3 style={{ margin: '0 0 8px' }}>Select a patient to start consult</h3>
          <p className="hint" style={{ maxWidth: 480, margin: '0 auto 16px' }}>
            Search by name, UHID, or phone above — or open EMR from <strong>My OPD Queue</strong> after check-in.
            Clinical documentation, Rx, and lab orders unlock once a patient with UHID is linked.
          </p>
          <Link to="/doctor/queue" className="btn btn-primary btn-sm">Go to My OPD Queue</Link>
        </div>
      )}

      {consultReady && !readOnly && (
        <WhatsappScribePanel
          value={form.scribeNote}
          onChange={(scribeNote) => setForm((f) => ({ ...f, scribeNote }))}
          onApplyToComplaint={(text) => setForm((f) => ({ ...f, complaint: f.complaint ? `${f.complaint}\n${text}` : text }))}
        />
      )}

      {consultReady && !readOnly && (

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 12, marginBottom: 12 }}>

          <DocAssistPanel
            complaint={form.complaint}
            age={form.patientAge}
            gender={form.patientGender || form.gender}
            allergies={form.history?.allergies || form.allergies || ''}
            onApply={applyDocAssist}
          />

          <div>

            <PediatricDoseCalculator onApply={applyPediatricDose} />

          </div>

        </div>

      )}



      {consultReady && (
      <div className="emr-workspace">

        <div className="emr-main-col">

          <nav className="emr-tabs card">

            {emrTabs.map((t) => (

              <button

                key={t.id}

                type="button"

                className={`emr-tab ${tab === t.id ? 'active' : ''}`}

                onClick={() => setTab(t.id)}

              >

                {t.label}

              </button>

            ))}

          </nav>



          <div className="card emr-panel-wrap">

            <EmrTabPanel tab={tab} form={form} setForm={setForm} readOnly={readOnly} inventory={inventory} labCatalog={labCatalog} genieMeds={genieMeds} emrSettings={emrSettings} />

            {doctorHasDentalWorkspace(user, emrSettings) && (
              <div style={{ marginTop: 12 }}>
                <DentalChart
                  value={form.dentalChart || {}}
                  onChange={(dentalChart) => setForm((f) => ({ ...f, dentalChart }))}
                  readOnly={readOnly}
                />
              </div>
            )}
            {emrSettings.enableHealthAssessment && (
              <HealthAssessmentPanel
                value={form.healthAssessment || {}}
                onChange={(healthAssessment) => setForm((f) => ({ ...f, healthAssessment }))}
                readOnly={readOnly}
              />
            )}
            {emrSettings.enableOphthalmology && (
              <OphthalmologyPanel
                value={form.ophthalmology || {}}
                onChange={(ophthalmology) => setForm((f) => ({ ...f, ophthalmology }))}
                readOnly={readOnly}
              />
            )}
            {(user?.role === 'doctor' || user?.role === 'manager') && (
              <PrivateNotesPanel
                value={form.privateNotes}
                onChange={(privateNotes) => setForm((f) => ({ ...f, privateNotes }))}
                readOnly={readOnly}
                doctorName={user?.name}
              />
            )}

          </div>



          {!readOnly && (

            <div className="emr-action-bar card">

              <div className="row-actions">

                <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => saveEmr(false)}>Save EMR</button>

                <button type="button" className="btn btn-secondary" disabled={busy} onClick={autoFillSoap}>Auto-fill SOAP</button>

                <button type="button" className="btn btn-secondary" disabled={busy} onClick={pauseConsult}>Pause consult</button>

                <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setShowCert(true)}>Medical certificate</button>

                <button type="button" className="btn btn-secondary" disabled={busy} onClick={bookFollowUp}>Book follow-up</button>

                <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setShowPrint(true)}>Print Rx</button>

                <button type="button" className="btn btn-secondary" disabled={busy} onClick={sendPharmacy}>→ Pharmacy</button>

                <button type="button" className="btn btn-secondary" disabled={busy} onClick={sendLab}>→ Laboratory</button>

                <button type="button" className="btn btn-secondary" disabled={busy} onClick={sendRadiology}>→ Radiology</button>

                <button type="button" className="btn btn-primary" disabled={busy} onClick={() => saveEmr(true)}>Complete visit</button>

              </div>

              {msg && <p className={`hint emr-msg ${msg.includes('not found') || msg.includes('Could not') ? 'error' : ''}`}>{msg}</p>}

            </div>

          )}

          {readOnly && msg && <p className="hint emr-msg card">{msg}</p>}

        </div>



        <EmrMagicBox
          patient={patientRecord}
          visits={pastVisits}
          queuePreVitals={queuePreVitals}
          onLoadVisit={readOnly ? undefined : copyFromVisit}
          patientId={form.patientId}
          showPrivate={user?.role === 'doctor' || user?.role === 'manager'}
          doctorName={user?.name}
        />

      </div>
      )}



      {showCert && !readOnly && consultReady && (

        <MedicalCertificateModal
          form={form}
          doctor={user}
          hospital={hospitalProfile || { name: user?.hospital }}
          docTemplates={emrSettings.docTemplates || []}
          persist
          onClose={() => setShowCert(false)}
        />

      )}



      {showPrint && !readOnly && consultReady && (

        <EmrPrintPrescription
          form={form}
          doctor={user}
          hospital={hospitalProfile || { name: user?.hospital }}
          emrSettings={printSettings}
          onClose={() => setShowPrint(false)}
        />

      )}

    </PageShell>

  );

}


